import i18n from '@/general/i18n';

export type MultipleFileSelectionHandler = (files: Array<File>) => void;
export type SingleFileSelectionHandler = (file: File) => void;

export const ACCEPT_ALL = '*';
export const ACCEPT_IMAGES = 'image/*';
export const ACCEPT_MEDIA = 'image/*,video/*,audio/*,application/pdf';
export const ACCEPT_PDF = 'application/pdf';

/**
 * Opens the native file selection modal.
 *
 * NOTE: Unfortunately we need to use a callback function here instead of returning a Promise since there's no reliable
 * way to detect when the user cancels the modal, which would otherwise result in the promise never resolving/rejecting.
 */
function doSelectFiles(onSelection: MultipleFileSelectionHandler, accept: string, multiple: boolean): void {
  const inputEl = document.createElement('input');
  inputEl.type = 'file';
  inputEl.accept = accept;
  inputEl.multiple = multiple;
  inputEl.addEventListener('change', () => {
    if (inputEl.files?.length) {
      onSelection([...inputEl.files]);
    }
  });
  inputEl.click();
}

export function selectFile(onSelection: SingleFileSelectionHandler, accept = ACCEPT_ALL): void {
  doSelectFiles(files => onSelection(files[0]), accept, false);
}

export function selectFiles(onSelection: MultipleFileSelectionHandler, accept = ACCEPT_ALL, multiple = true): void {
  doSelectFiles(onSelection, accept, multiple);
}

export function checkFileType(type: string, accept: string): boolean {
  const acceptTypes = accept.split(',');
  return acceptTypes.some(acceptType => {
    if (acceptType.includes('*')) {
      const [typePrefix] = acceptType.split('/');
      return type.startsWith(typePrefix);
    } else return acceptType === type;
  });
}

export function getAllowedFilesDescription(accept: string): string {
  const acceptTypes = accept.split(',');
  return acceptTypes.map(type => getFileTypeLabel(type)).join(', ');
}

function getFileTypeLabel(type: string): string {
  switch (type) {
    case ACCEPT_ALL:
      return i18n.global.t('fileUpload.allFileTypes');
    case ACCEPT_IMAGES:
      return i18n.global.t('fileUpload.allImageTypes');
    case ACCEPT_PDF:
      return 'PDF';
    case 'image/jpeg':
      return 'JPEG';
    case 'image/png':
      return 'PNG';
    case 'image/tif':
      return 'TIF';
    case 'image/tiff':
      return 'TIFF';
    case 'audio/wav':
      return 'WAV';
    case 'audio/mp3':
      return 'MP3';
    case 'audio/mpeg':
      return 'MPEG';
    case 'video/mp4':
      return 'MP4';
    case 'video/quicktime':
      return 'Quicktime';
    default:
      return type;
  }
}